import {
  ArrowLeft,
  DotsThreeVertical,
  PencilSimple,
  Speedometer,
  Trash,
} from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import {
  Asset,
  DocumentFetchPresignedUrlsRequest,
  DocumentObjectType,
  TrailerDocumentSubType,
  TruckDocumentSubType,
} from '../../../../../types/apiTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import CustomTabMenu from '../../../../../shared/CustomTabMenu';
import AssetMaintenance from './widgets/asset-details/AssetMaintenance';
import AssetOverview from './widgets/asset-details/AssetOverview';
import AssetForm from './widgets/asset-form/AssetForm';
import { AssetView } from './widgets/asset-form/types';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { toast } from 'react-hot-toast';
import SubmitMileageUnitModal from './widgets/asset-details/modals/SubmitMileageUnitModal';
import { useCatenaConnected } from './catenaMileage';

interface AssetDetailsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  selectedAsset: any | null;
  setSelectedAsset: (asset: Asset | null) => void;
  onAssetChange?: () => void;
}

function AssetDetails({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  selectedAsset,
  setSelectedAsset,
  onAssetChange,
}: AssetDetailsProps) {
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [assetImageUrl, setAssetImageUrl] = useState<string>('');
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const { asset_type, asset_id } = useParams();
  const [selectedMenuTab, setSelectedMenuTab] = useState<string>('overview');
  const location = useLocation();
  const [popupVisible, setPopupVisible] = useState(false);
  const [isSubmitMileageOpen, setIsSubmitMileageOpen] = useState(false);

  useEffect(() => {
    const fetchAssetDetails = async (
      asset_id: string,
      asset_type: 'truck' | 'trailer'
    ) => {
      setLoading(true);
      try {
        let data: any;
        switch (asset_type) {
          case 'truck':
            const responseTruck = await callProtectedApi(
              `/assets/type/${asset_type}/${asset_id}`,
              'get',
              {},
              'Error occurred while fetching your truck asset.'
            );
            data = responseTruck?.data?.data?.asset;
            if (
              data &&
              Object.entries(data)?.length > 0 &&
              responseTruck?.status === HttpStatusCode.Ok
            ) {
              setSelectedAsset(data);
            }
            break;
          case 'trailer':
            const response = await callProtectedApi(
              `/assets/type/${asset_type}/${asset_id}`,
              'get',
              {},
              'Error occurred while fetching your trailer asset.'
            );
            data = response?.data?.data?.asset;
            if (
              data &&
              Object.entries(data)?.length > 0 &&
              response?.status === HttpStatusCode.Ok
            ) {
              setSelectedAsset(data);
            }
            break;
        }
      } catch (e: any) {
        console.error('Error occurred while fetching your asset: ', e?.message);
      } finally {
        setLoading(false);
      }
    };
    if (asset_type && asset_id && selectedAsset === null) {
      fetchAssetDetails(asset_id, asset_type as 'truck' | 'trailer');
      fetchAssetImage(asset_type, asset_id);
    } else if (selectedAsset !== null) {
      fetchAssetImage(
        selectedAsset?.asset_type,
        selectedAsset?.asset_type === 'truck'
          ? selectedAsset?.truck_id
          : selectedAsset?.trailer_id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset_type, asset_id]);

  useEffect(() => {
    if (location.pathname.includes('overview')) {
      setSelectedMenuTab('overview');
    } else if (location.pathname.includes('maintenance')) {
      setSelectedMenuTab('maintenance');
    } else if (location.pathname.includes('edit')) {
      setSelectedMenuTab('edit');
    }
  }, [location.pathname]);

  // Re-fetch image when selectedAsset changes to respect show_asset_image
  useEffect(() => {
    if (selectedAsset && selectedAsset.asset_type) {
      const assetId =
        selectedAsset.asset_type === 'truck'
          ? selectedAsset.truck_id
          : selectedAsset.trailer_id;

      if (assetId) {
        fetchAssetImage(selectedAsset.asset_type, assetId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset?.show_asset_image]);

  const updateAssetImageUrl = (url: string) => {
    setAssetImageUrl(url);
  };

  // Bridges AssetForm's narrow AssetView back to the parent's full Asset.
  // The runtime value is the same `/assets/type/:type/:id` payload this
  // component already stores as Asset, so the cast is safe.
  const handleFormSetSelectedAsset = (asset: AssetView) => {
    setSelectedAsset(asset as unknown as Asset);
  };

  const handleSetSelectedMenuTab = (tab: string) => {
    setSelectedMenuTab(tab);
  };

  const fetchAssetImage = async (assetType: string, assetId: string) => {
    // Check if we should show the image
    if (selectedAsset && !selectedAsset.show_asset_image) {
      setAssetImageUrl('');
      return;
    }

    try {
      switch (assetType) {
        case 'trailer':
          const responseDocUrl = await callProtectedApi(
            `/documents/display/${DocumentObjectType.Trailer}/${assetId}`,
            'post',
            {
              document_count: 1,
              document_sub_type: TrailerDocumentSubType.Images,
            } as DocumentFetchPresignedUrlsRequest
          );
          const url: string[] = responseDocUrl?.data?.data?.document_urls;
          setAssetImageUrl(url?.length > 0 ? url[0] : '');

          break;
        case 'truck':
          const responseDocUrlTruck = await callProtectedApi(
            `/documents/display/${DocumentObjectType.Truck}/${assetId}`,
            'post',
            {
              document_count: 1,
              document_sub_type: TruckDocumentSubType.Images,
            } as DocumentFetchPresignedUrlsRequest
          );
          const urlTruck = responseDocUrlTruck?.data?.data?.document_urls;
          setAssetImageUrl(urlTruck?.length > 0 ? urlTruck[0] : '');
      }
    } catch (error: any) {
      console.error('Error occurred fetching asset image: ', error?.message);
    }
  };

  const deleteAsset = useCallback(
    async (assetType: string, assetId: string) => {
      setLoadingDelete(true);
      try {
        const response = await callProtectedApi(
          `/assets/type/${assetType}/${assetId}`,
          'delete',
          {},
          'Error occurred while deleting asset.'
        );
        if (response?.status === HttpStatusCode.Ok) {
          toast.success('Asset deleted successfully');
          onAssetChange?.();
          navigate('/dashboard/assets');
        }
      } catch (e: any) {
        console.error('Error deleting asset:', e?.message);
      } finally {
        setLoadingDelete(false);
      }
    },
    [callProtectedApi, navigate, onAssetChange]
  );

  const props = {
    userType,
    currentPage,
    currentTab,
    setCurrentTab,
    subTab,
    setSubTab,
    selectedAsset,
    setSelectedAsset,
    setAssetImageUrl: updateAssetImageUrl,
    assetImageUrl,
  };

  const MoreMenuOptions = useCallback(() => {
    return (
      <button
        onClick={() => setPopupVisible(!popupVisible)}
        className="relative flex h-[48px] w-[48px] items-center justify-center rounded-full border-[1px] border-[#DADADA] bg-white"
      >
        <DotsThreeVertical size={28} />
        {popupVisible && (
          <div className="absolute right-0 top-[2.85rem] z-10 w-[184px] rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl">
            <div>
              <button
                onClick={(e: any) => {
                  e.stopPropagation();
                  navigate(
                    `/dashboard/assets/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/edit`
                  );
                  setPopupVisible(false);
                }}
                className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
              >
                <PencilSimple size={20} />
                <p>Edit</p>
              </button>
              {selectedAsset?.mileageRequest?.status === 'pending' && (
                <button
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setIsSubmitMileageOpen(true);
                  }}
                  className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                >
                  <Speedometer size={20} />
                  <p>Submit Mileage</p>
                </button>
              )}
              <button
                onClick={async (e: any) => {
                  e.stopPropagation();
                  await deleteAsset(
                    selectedAsset?.asset_type,
                    selectedAsset?.asset_type === 'truck'
                      ? selectedAsset?.truck_id
                      : selectedAsset?.trailer_id
                  );
                  setPopupVisible(false);
                }}
                className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
              >
                <Trash size={20} />
                <p>Remove</p>
              </button>
            </div>
          </div>
        )}
      </button>
    );
  }, [
    deleteAsset,
    navigate,
    popupVisible,
    selectedAsset?.asset_type,
    selectedAsset?.mileageRequest?.status,
    selectedAsset?.trailer_id,
    selectedAsset?.truck_id,
  ]);

  // THT-678: the per-asset submit modal must lock mileage entry while the
  // customer is Catena-connected. Resolve the customer-level flag once here
  // (single source of truth) and drill it into the modal instead of the modal
  // fetching its own — gated to assets that actually expose the Submit Mileage
  // entry point so it doesn't fire on every asset view.
  const catenaConnected = useCatenaConnected(
    selectedAsset?.mileageRequest?.status === 'pending'
  );

  if (!selectedAsset || loading || loadingDelete) {
    return (
      <ClipLoader
        color="#1D213B"
        loading={true}
        size={60}
        aria-label="Loading Spinner"
        data-testid="loader"
        className="mt-10 flex h-full w-full justify-center"
      />
    );
  }

  // Identical across all three breakpoint layouts below — defined once.
  const editAssetRoute = (
    <Route
      path="edit"
      element={
        <AssetForm
          mode="edit"
          selectedAsset={selectedAsset}
          setSelectedAsset={handleFormSetSelectedAsset}
          assetImageUrl={assetImageUrl}
          setAssetImageUrl={updateAssetImageUrl}
        />
      }
    />
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="h-full w-[1148px] max-w-[1148px] overflow-y-auto overflow-x-hidden">
            <div className="flex h-full w-full flex-col space-y-[16px] pt-[16px]">
              {!location.pathname?.includes('edit') &&
                selectedAsset &&
                selectedAsset?.asset_type?.length > 0 && (
                  <div className="flex flex-row items-center justify-between align-middle">
                    <CustomTabMenu
                      prefixPath="/dashboard/assets"
                      menuOptions={[
                        {
                          label: 'Overview',
                          value: 'overview',
                          path: `/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/overview`,
                        },
                        {
                          label: 'Maintenance',
                          value: 'maintenance',
                          path: `/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/maintenance`,
                        },
                      ]}
                      setSelectedMenuTab={handleSetSelectedMenuTab}
                      selectedMenuTab={selectedMenuTab}
                      screenView={screenView}
                    />
                    {selectedMenuTab === 'overview' && <MoreMenuOptions />}
                  </div>
                )}
              <div className="flex flex-row items-center space-x-1 align-middle">
                <ArrowLeft
                  size={20}
                  weight="bold"
                  className="hover:cursor-pointer"
                  onClick={() => navigate('/dashboard/assets')}
                />
                <img
                  alt={`${selectedAsset?.asset_type}-svg`}
                  src={`/${selectedAsset?.asset_type}.svg`}
                  className="h-[24px] w-[24px] text-primary"
                />
                <h2 className="pl-2 text-left text-[20px] font-semibold text-primary">
                  {selectedAsset?.truck_display_name ||
                    selectedAsset?.trailer_display_name}
                </h2>
              </div>
              {selectedAsset &&
                selectedAsset?.asset_type?.length > 0 &&
                (selectedAsset?.truck_id?.length > 0 ||
                  selectedAsset?.trailer_id?.length > 0) && (
                  <Routes>
                    <Route
                      path="overview"
                      element={<AssetOverview {...props} />}
                    />
                    <Route
                      path="maintenance"
                      element={<AssetMaintenance {...props} />}
                    />
                    {editAssetRoute}
                  </Routes>
                )}
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div className="h-full w-[568px] max-w-[568px] overflow-y-auto overflow-x-hidden">
            <div className="flex h-full w-full flex-col space-y-[16px] py-[16px]">
              {!location.pathname?.includes('edit') &&
                selectedAsset &&
                selectedAsset?.asset_type?.length > 0 && (
                  <div className="flex flex-row items-center justify-between align-middle">
                    <CustomTabMenu
                      prefixPath="/dashboard/assets"
                      menuOptions={[
                        {
                          label: 'Overview',
                          value: 'overview',
                          path: `/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/overview`,
                        },
                        {
                          label: 'Maintenance',
                          value: 'maintenance',
                          path: `/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/maintenance`,
                        },
                      ]}
                      setSelectedMenuTab={handleSetSelectedMenuTab}
                      selectedMenuTab={selectedMenuTab}
                      screenView={screenView}
                    />
                    {selectedMenuTab === 'overview' && <MoreMenuOptions />}
                  </div>
                )}
              <div className="flex flex-row items-center space-x-1 align-middle">
                <ArrowLeft
                  size={20}
                  weight="bold"
                  className="hover:cursor-pointer"
                  onClick={() => navigate('/dashboard/assets')}
                />
                <img
                  alt={`${selectedAsset?.asset_type}-svg`}
                  src={`/${selectedAsset?.asset_type}.svg`}
                  className="h-[24px] w-[24px] text-primary"
                />
                <h2 className="pl-2 text-left text-[20px] font-semibold text-primary">
                  {selectedAsset?.truck_display_name ||
                    selectedAsset?.trailer_display_name}
                </h2>
              </div>
              {selectedAsset &&
                selectedAsset?.asset_type?.length > 0 &&
                (selectedAsset?.truck_id?.length ||
                  selectedAsset?.trailer_id?.length > 0) && (
                  <Routes>
                    <Route
                      path="overview"
                      element={<AssetOverview {...props} />}
                    />
                    <Route
                      path="maintenance"
                      element={<AssetMaintenance {...props} />}
                    />
                    {editAssetRoute}
                  </Routes>
                )}
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div className="h-full w-[344px] max-w-[344px] overflow-y-auto overflow-x-hidden">
            <div className="flex h-full w-full flex-col space-y-[16px] py-[16px]">
              {!location.pathname?.includes('edit') &&
                selectedAsset &&
                selectedAsset?.asset_type?.length > 0 && (
                  <div className="flex flex-row items-center justify-between align-middle">
                    <CustomTabMenu
                      prefixPath="/dashboard/assets"
                      menuOptions={[
                        {
                          label: 'Overview',
                          value: 'overview',
                          path: `/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/overview`,
                        },
                        {
                          label: 'Maintenance',
                          value: 'maintenance',
                          path: `/${selectedAsset?.asset_type}/${selectedAsset?.asset_type === 'truck' ? selectedAsset?.truck_id : selectedAsset?.trailer_id}/maintenance`,
                        },
                      ]}
                      setSelectedMenuTab={handleSetSelectedMenuTab}
                      selectedMenuTab={selectedMenuTab}
                      screenView={screenView}
                    />
                    {selectedMenuTab === 'overview' && <MoreMenuOptions />}
                  </div>
                )}
              <div className="flex flex-row items-center space-x-1 align-middle">
                <ArrowLeft
                  size={20}
                  weight="bold"
                  className="hover:cursor-pointer"
                  onClick={() => navigate('/dashboard/assets')}
                />
                <img
                  alt={`${selectedAsset?.asset_type}-svg`}
                  src={`/${selectedAsset?.asset_type}.svg`}
                  className="h-[24px] w-[24px] text-primary"
                />
                <h2 className="pl-2 text-left text-[20px] font-semibold text-primary">
                  {selectedAsset?.truck_display_name ||
                    selectedAsset?.trailer_display_name}
                </h2>
              </div>
              {selectedAsset &&
                selectedAsset?.asset_type?.length > 0 &&
                (selectedAsset?.truck_id?.length ||
                  selectedAsset?.trailer_id?.length) && (
                  <Routes>
                    <Route
                      path="overview"
                      element={<AssetOverview {...props} />}
                    />
                    <Route
                      path="maintenance"
                      element={<AssetMaintenance {...props} />}
                    />
                    {editAssetRoute}
                  </Routes>
                )}
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {renderScreenView(screenView)}
      <SubmitMileageUnitModal
        isOpen={isSubmitMileageOpen}
        onClose={() => setIsSubmitMileageOpen(false)}
        selectedAsset={selectedAsset}
        catenaConnected={catenaConnected}
      />
    </>
  );
}

export default AssetDetails;
